import type { ChartApiService } from '@atlassian/conversation-assistant-viz/services/chart-api-service';

/**
 * Replaces [^chart^] placeholders in content with chart smart links.
 * For each placeholder, it extracts the corresponding chart config from markdownData,
 * generates a chart link via the Chart API, and replaces the placeholder with the link.
 *
 * @param content - The content string that may contain [^chart^] placeholders
 * @param markdownData - Record containing chart configs with keys like "chart-0", "chart-1", etc.
 * @param chartApiService - Instance of ChartApiService to generate chart links
 * @returns Promise resolving to content with placeholders replaced by smart links
 */
export async function replaceChartPlaceholdersWithSmartLinks(
	content: string,
	markdownData: Record<string, string> | undefined,
	chartApiService: ChartApiService,
): Promise<string> {
	if (!markdownData || !content.includes('[^chart^]')) {
		return content;
	}

	const chartPlaceholderRegex = /\[\^chart\^\]/g;
	const matches = Array.from(content.matchAll(chartPlaceholderRegex));

	if (matches.length === 0) {
		return content;
	}

	let result = content;
	let chartIndex = 0;

	for (let i = 0; i < matches.length; i++) {
		const chartConfigKey = `chart-${chartIndex}`;
		const chartConfigJson = markdownData[chartConfigKey];

		if (chartConfigJson) {
			try {
				const chartConfig = JSON.parse(chartConfigJson);

				const chartLinkResponse = await chartApiService.generateChartLink({
					chartType: chartConfig.chartType,
					data: chartConfig.data,
					chartTitle: chartConfig.chartTitle,
					subtitle: chartConfig.subtitle,
					xAccessor: chartConfig.xAccessor,
					yAccessor: chartConfig.yAccessor,
					labelAccessor: chartConfig.labelAccessor,
					valueAccessor: chartConfig.valueAccessor,
					colorAccessor: chartConfig.colorAccessor,
					metricsConfig: chartConfig.metricsConfig,
				});

				result = result.replace('[^chart^]', chartLinkResponse.link);
			} catch {
				// If chart link generation fails, leave the placeholder as is
			}
		}

		chartIndex++;
	}

	return result;
}
